import { useEffect, useState } from "react";
import FormPrint from "./FormPrint";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useQuery } from "../../../utils/hooks";
import { errorToastMessage } from "../../../utils/toast";
import { getFormResponseData } from "../../../utils/response";
import http from "../../../utils/http";
import httpSurvey from "../../../utils/httpSurvey";
import { SurveyBySlugRoot } from "../../../types/SurveyBySlug.types";
import i18n from "../../../i18n";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "../../../i18n/constants";

const SurveyPrint = () => {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<any[]>([]);
  const [languages, setLanguages] = useState<
    { languageCode: string; languageLabel: string }[]
  >([]);

  const studyId = useQuery().get("studyId") || undefined;
  const formId = useQuery().get("formId") || undefined;
  const participantId = useQuery().get("participantId") || undefined;
  const surveyAssignmentId = useQuery().get("surveyAssignmentId") || undefined;
  const screeningAttemptId = useQuery().get("screeningAttemptId") || undefined;
  const category = useQuery().get("category") || "survey";
  const surveySlug = useQuery().get("surveySlug") || undefined;
  const initialLangCode = useQuery().get("langCode") || undefined;
  const [langCode, setLangCode] = useState(initialLangCode);
  const isAdminContext = !surveySlug && !!studyId;
  const tPrint = i18n.getFixedT(
    (SUPPORTED_LANGUAGES as readonly string[]).includes(langCode || "")
      ? (langCode as string)
      : DEFAULT_LANGUAGE,
    "responses",
  );
  const printLanguageLabel = tPrint("SURVEY.PRINT_LANGUAGE_LABEL") as string;

  // This page opens in its own tab (window.open), outside StudyLayout, so
  // state.study.languages isn't populated here — fetch the study's languages directly.
  useEffect(() => {
    if (!isAdminContext) {
      return;
    }
    const fetchLanguages = async () => {
      try {
        const { data } = await http.get(`/study/${studyId}/settings/languages`);
        setLanguages(data?.data || []);
      } catch {
        // Non-critical: selector just falls back to English-only.
      }
    };
    fetchLanguages();
  }, [studyId, isAdminContext]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = [];
        if (formId) {
          const { questions, formName, id } = await getFormResponseData({
            selectedForm: {
              id: formId,
            },
            studyId,
            participantId,
            surveyAssignmentId,
            screeningAttemptId,
            surveySlug,
            langCode,
          });

          data.push({
            id,
            name: formName,
            questions,
          });
        } else {
          const phasesList: any[] = [];
          let res;
          if (surveyAssignmentId && studyId && participantId) {
            res = await http.get(
              `/study/${studyId}/survey-responses/${surveyAssignmentId}?participantId=${participantId}`,
            );
          } else if (surveySlug) {
            res = await httpSurvey.get<SurveyBySlugRoot>(
              `/survey/${surveySlug}`,
            );
          } else if (category === "screening" && studyId && participantId) {
            res = await http.get(
              `/study/${studyId}/study-phase/participant/screening`,
              { params: { participantId, isAdmin: true } },
            );
          } else if (category === "visit" && studyId && participantId) {
            res = await http.get(`/study/${studyId}/study-phase/participant`, {
              params: { participantId },
            });
          }

          if (category === "screening" || category === "visit") {
            res?.data?.data?.forEach((phase: any) => {
              const phaseForms = phase.phaseForms
                .map((phaseForm: any) => ({
                  id: phaseForm.form.id,
                  name: phaseForm.form.name,
                  position: phaseForm.position,
                }))
                .sort((a: any, b: any) => a.position - b.position);

              phasesList.push({
                id: phase.id,
                name: phase.name,
                position: phase.position,
                phaseForms,
              });
            });
          } else {
            res?.data?.data.package.surveyLinks?.forEach((link: any) => {
              if (link.phase) {
                const phaseForms = link.phase.phaseForms
                  .map((phaseForm: any) => ({
                    id: phaseForm.form.id,
                    name: phaseForm.form.name,
                    position: phaseForm.position,
                  }))
                  .sort((a: any, b: any) => a.position - b.position);

                phasesList.push({
                  id: link.phase.id,
                  name: link.phase.name,
                  position: link.position,
                  phaseForms,
                });
              }
            });
          }

          phasesList.sort((a, b) => a.position - b.position);

          let position = 1;
          for (let i = 0; i < phasesList.length; i++) {
            const phase = phasesList[i];
            for (let j = 0; j < phase.phaseForms.length; j++) {
              const form = phase.phaseForms[j];

              const { questions, formName, id } = await getFormResponseData({
                selectedForm: {
                  id: form.id,
                },
                studyId,
                participantId,
                surveyAssignmentId,
                screeningAttemptId,
                surveySlug,
                langCode,
              });

              data.push({
                id,
                name: position++ + ". " + phase.name + " : " + formName,
                questions,
              });
            }
          }
        }

        setForms(data);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, [
    setLoading,
    formId,
    studyId,
    participantId,
    surveyAssignmentId,
    screeningAttemptId,
    category,
    surveySlug,
    langCode,
  ]);

  const startPrint = () => {
    window.print();
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <CircularProgress size={50} />
      <div style={{ marginTop: "10px" }}>
        {tPrint("SURVEY.PRINT_LOADING") as string}
      </div>
    </div>
  ) : (
    // surveyError ? (
    //   <div>Something went wrong. please try again sometime later.</div>
    // ) :
    <div className="study-print-container">
      <div
        className="print-button"
        style={{ alignItems: "center", gap: "16px" }}
      >
        {isAdminContext && (
          <FormControl sx={{ width: "160px" }}>
            <InputLabel id="print-response-language-label">
              {printLanguageLabel}
            </InputLabel>
            <Select
              value={langCode || "en"}
              onChange={(e) => setLangCode(e.target.value)}
              id="printResponseLang"
              label={printLanguageLabel}
              labelId="print-response-language-label"
              sx={{ height: "36px" }}
            >
              {(languages.length
                ? languages
                : [{ languageCode: "en", languageLabel: "English" }]
              ).map((lang) => (
                <MenuItem key={lang.languageCode} value={lang.languageCode}>
                  {lang.languageLabel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="contained" onClick={startPrint}>
          {tPrint("SURVEY.PRINT") as string}
        </Button>
      </div>
      {forms?.map((form, i) => (
        <FormPrint
          key={form.id}
          form={form}
          breakPage={i === 0 ? false : true}
          langCode={langCode}
        />
      ))}
    </div>
  );
};

export default SurveyPrint;
